"use client";
import {
  CheckboxComponent,
  DividerComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
} from "@/components";
import LimitedOfferReminder from "@/components/limited-offer-reminder";
import { useRouter } from "@/i18n/routing";
import {
  createInsuranceUrl,
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  GIFS,
  IMAGES,
  PRODUCT_HANDLER,
  PRODUCTS,
  STAGE,
  WEB_COLORS,
} from "@/lib/constants";
import {
  useGetApplicationQuery,
  useGetESignQuery,
  useGetInsuranceQuery,
  useUpdateInsuranceMutation,
} from "@/lib/redux";
import {
  setInsuranceDeclaration,
  setSelectedPlans,
  resetPlans,
  setTotalPlans,
} from "@/lib/redux/slice/InsuranceSlice";
import { ArrowDropDownRounded } from "@mui/icons-material";
import {
  Box,
  Divider,
  Grid,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { keyframes } from "@mui/system";

const InsurancePlan = ({
  offerReminder,
  handleLimitedOfferModal,
  handleContinueWithInsurance,
  handleContinueWithoutInsurance,
  refetchESignData,
  showDetails = true,
  editOptionToggle = true,
  backgroundColor,
  enableKnowMore = true,
  setNomineeDetailsModalVisible,
}) => {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isInsuranceOpen, setInsuranceOpen] = useState(true);
  const productType = useSelector((s) => s.applicationSlice.productType);
  const [isLoading, setIsLoading] = useState(false);
  const [insurances, setInsurances] = useState([]);
  const [protectionPlan, setProtectionPlan] = useState(true);
  const [amount, setAmount] = useState(0);
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { insuranceDeclaration, selectedPlans, insuranceSelected, totalPlans } =
    useSelector((s) => s.insurance);
  const { offerSelectedData } = useSelector((s) => s.offerselection);

  const {
    data: applicationData,
    isLoading: fetchingApplications,
    error: applicationFetchError,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[productType],
    applicationData?.data,
  );

  // const {
  //   data: eSignData,
  //   error: errorESign,
  //   isLoading: eSignLoading,
  //   isError: isErrorESign,
  //   refetch: refetchESignData,
  // } = useGetESignQuery(
  //   { appId: productApplication?.appId },
  //   {
  //     skip:
  //       !productApplication?.appId &&
  //       productApplication?.stage !== STAGE.E_SIGN,
  //   },
  // );
  const [
    insuranceUpdate,
    {
      data: insuranceUpdateData,
      isLoading: loadingInsuranceUpdateData,
      error: insuranceUpdateDataError,
      isSuccess: successInsuranceUpdateData,
    },
  ] = useUpdateInsuranceMutation();

  const shouldFetch =
    Boolean(productApplication?.appId) &&
    (productApplication?.stage === STAGE.OFFERS
      ? Boolean(
          offerSelectedData?.amount &&
          offerSelectedData?.id &&
          offerSelectedData?.tenure,
        )
      : true);

  const queryArgs = useMemo(() => {
    if (!shouldFetch) return undefined;
    // setIsLoading(true);
    if (productApplication?.stage === STAGE.OFFERS) {
      const params = {
        offerId: offerSelectedData?.id,
        newRequestedLoanAmount: `${offerSelectedData?.amount}`,
        selectedTenure: offerSelectedData?.tenure,
      };
      const url = createInsuranceUrl(params);
      return {
        appId: productApplication?.appId,
        params: url,
      };
    } else {
      return {
        appId: productApplication?.appId,
      };
    }
  }, [productApplication?.appId, offerSelectedData, shouldFetch]);

  const {
    data: insuranceData,
    isLoading: loadingInsuranceData,
    isFetching: fetchingInsuranceData,
    error: insuranceLoadingError,
    refetch: refetchInsuranceData,
  } = useGetInsuranceQuery(queryArgs, {
    skip: !shouldFetch || !queryArgs,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  /***************************************EFFECT************************************ */

  useEffect(() => {
    setIsLoading(
      fetchingApplications || loadingInsuranceData || fetchingInsuranceData,
    );
  }, [fetchingApplications, loadingInsuranceData, fetchingInsuranceData]);
  useEffect(() => {
    const isActive = insuranceData?.data?.isActive;

    if (isActive === undefined) return;

    handleProtectionInsurance(isActive);

    if (!isActive) {
      const showNomineeModal =
        sessionStorage.getItem("nominee_modal") === "true";

      if (showNomineeModal) {
        sessionStorage.removeItem("nominee_modal");
        setNomineeDetailsModalVisible(false);
      }
    }
  }, [insuranceData?.data?.isActive]);

  useEffect(() => {
    if (insuranceData?.data) {
      let newInsurances = insuranceData?.data?.insuranceList?.map(
        (insurance) => {
          return {
            id: insurance?.id,
            title: insurance?.title,
            validity: insurance?.validity,
            amount: insurance?.amount,
            isActive: insurance?.isActive,
          };
        },
      );
      setInsurances(newInsurances);
      setAmount(insuranceData?.data?.allInsurance);
    }
  }, [insuranceData?.data]);

  const handleProtectionInsurance = (isChecked) => {
    let n = insuranceData?.data?.insuranceList?.length;
    const arr = Array.from({ length: n }, (_, i) => i + 1);
    if (!totalPlans) dispatch(setTotalPlans(arr));
    if (!isChecked) {
      dispatch(resetPlans());
    } else {
      dispatch(setSelectedPlans(arr));
    }
  };

  const handleInsuranceUpdate = async (payload) => {
    try {
      setIsLoading(true);

      await insuranceUpdate(payload).unwrap();

      if (productApplication?.stage === STAGE.E_SIGN && refetchESignData) {
        await refetchESignData();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const INSURANCE_BENEFITS = [
    {
      icon: insuranceSelected
        ? IMAGES.filledCalendarInsuranceIcon
        : IMAGES.calendarInsuranceIcon,
      alt: "calendar icon",
      text: "EMI Cover for Job Loss & Critical Illness*",
    },
    {
      icon: insuranceSelected
        ? IMAGES.filledCoveredLoanInsuranceIcon
        : IMAGES.coveredLoanInsuranceIcon,
      alt: "Loan covered icon",
      text: "Loan Covered in Case of Death",
    },
    {
      icon: insuranceSelected
        ? IMAGES.filledOpdInsuranceIcon
        : IMAGES.opdInsuranceIcon,
      alt: "OPD icon",
      text: "Tele consultation & OPD Benefits",
    },
  ];
  return (
    <>
      <LoaderComponent open={isLoading} />
      <PaperComponent
        noDrop
        paperStyle={{
          borderRadius: "12px",
          my: 1,
          py: 0,
          px: 0,
          overflow: "hidden",
          minWidth: "290px",
        }}
      >
        <Stack>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            backgroundColor={"#E9FFE6"}
            width="100%"
            minHeight={{ xs: 80, md: 102 }}
            position="relative"
          >
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={{ md: 2 }}
              // px={{ xs: 2, md: 4 }}
            >
              <Image
                src={IMAGES.protectionPlanIcon}
                alt="protection plan icon"
                width={isMobile ? 95 : 126}
                height={isMobile ? 78 : 102}
              />
              <Box>
                <Typography
                  fontWeight={{ md: 700, xs: 500 }}
                  fontSize={{ md: "24px", xs: "14px" }}
                  color={"#004135"}
                >
                  Protection Plus
                </Typography>
                <Typography
                  fontWeight={500}
                  fontSize={{ md: "14px", xs: "8px", sm: "10px" }}
                  color={"#004135"}
                >
                  One Plan. Triple Protection.
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="column"
              justifyContent="flex-start"
              alignItems="center"
              gap="0px"
              sx={{
                mr: { xs: 1, md: 3 },
                mb: showDetails ? { xs: 3, md: 3.5 } : 0,
                zIndex: 2,
              }}
            >
              {editOptionToggle && (
                <>
                  {!insuranceData?.data?.isActive ? (
                    <Image
                      src={GIFS.toggle}
                      alt="toggle gif"
                      width={48}
                      height={48}
                      style={{
                        marginRight: isMobile ? "4px" : "8px",
                      }}
                      onClick={async () => {
                        const arr = Array.from(
                          {
                            length:
                              insuranceData?.data?.insuranceList?.length || 0,
                          },
                          (_, i) => i + 1,
                        );

                        const payload = {
                          appId: productApplication.appId,
                          selectedPlan: arr,
                          selectOfferRequest: {
                            offerId: offerSelectedData?.id || 0,
                            newRequestLoanAmount:
                              offerSelectedData?.amount || 0,
                            selectedTenure: offerSelectedData?.tenure || 0,
                          },
                          insuranceConsent: false,
                          isConsentRequest: false,
                        };

                        handleProtectionInsurance(true);
                        await handleInsuranceUpdate(payload);
                        if (!isInsuranceOpen) {
                          setInsuranceOpen(true);
                        }
                      }}
                    />
                  ) : (
                    <Switch
                      size={isBelowMd ? "small" : "medium"}
                      checked={insuranceData?.data?.isActive}
                      onClick={async () => {
                        const checked = false;
                        const payload = {
                          appId: productApplication.appId,
                          selectedPlan: [],
                          selectOfferRequest: {
                            offerId: offerSelectedData?.id || 0,
                            newRequestLoanAmount:
                              offerSelectedData?.amount || 0,
                            selectedTenure: offerSelectedData?.tenure || 0,
                          },
                          insuranceConsent: false,
                          isConsentRequest: false,
                        };

                        handleProtectionInsurance(checked);
                        await handleInsuranceUpdate(payload);
                      }}
                    />
                  )}
                </>
              )}
            </Stack>
            {showDetails && (
              <Box
                sx={{
                  background:
                    "linear-gradient(90deg,  #E9FFE6 10%, #00F200 90%, rgba(0,242,0,0.9) 100%)",
                  px: 2,
                  py: 0.5,
                  mt: { md: 1, xs: 0.5 },
                  borderRadius: "0px",
                  minWidth: { xs: 90, md: 150 },
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  textAlign: "right",
                  zIndex: 1,
                }}
              >
                <Typography
                  fontWeight={400}
                  fontSize={{ md: "14px", xs: "10px" }}
                  color="#004135"
                  textAlign={"right"}
                >
                  Limited Offer
                </Typography>
              </Box>
            )}
          </Stack>
          <Stack
            backgroundColor={backgroundColor ? backgroundColor : "#F7FAFA"}
            px={{ xs: 2, md: 4 }}
            py={2}
          >
            <Grid
              container
              justifyContent="space-between"
              alignItems="stretch"
              color="#3F3F3F"
              columnSpacing={{ xs: 1, md: 2 }}
            >
              {INSURANCE_BENEFITS.map((item) => (
                <Grid item xs={4} key={item.text}>
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="8px"
                    border="1px solid #F0F0F0"
                    backgroundColor="#FFFFFF"
                    height={{ xs: 130, md: 144 }}
                    px={{ xs: 1, md: 2 }}
                  >
                    <Image
                      src={item.icon}
                      alt={item.alt}
                      width={isBelowMd ? 50 : 72}
                      height={isBelowMd ? 50 : 72}
                    />

                    <Typography
                      textAlign="center"
                      fontWeight={400}
                      fontSize={{ md: "14px", xs: "11px" }}
                      lineHeight={{ xs: "15px", md: "18px" }}
                      color="#565656"
                      mt={{ xs: 1, md: 2 }}
                    >
                      {item.text}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            {showDetails && <Divider sx={{ my: 2, borderColor: "#D8E1E1" }} />}

            <Stack>
              <Stack
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ cursor: "pointer" }}
              >
                <Box mt={!showDetails ? 2 : 1}>
                  <Typography
                    display="inline"
                    fontWeight={400}
                    fontSize={{ xs: "12px", md: "16px" }}
                    color="#606060"
                    mr="4px"
                  >
                    Get all three for just {CurrencyConverter(amount)} per
                    month*
                  </Typography>

                  {enableKnowMore && (
                    <Typography
                      display="inline"
                      fontWeight={700}
                      fontSize={{ xs: "12px", md: "16px" }}
                      color="#004135"
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        setIsLoading(true);
                        router.push(
                          `/${productType}/insurance-protection-plan`,
                        );
                      }}
                    >
                      Know More
                    </Typography>
                  )}
                </Box>

                {showDetails && (
                  <ArrowDropDownRounded
                    sx={{
                      color: "#004135",
                      transition: (theme) =>
                        theme.transitions.create("transform", {
                          duration: theme.transitions.duration.shortest,
                        }),
                      transform: isInsuranceOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transformOrigin: "center",
                    }}
                    onClick={() => setInsuranceOpen((o) => !o)}
                  />
                )}
              </Stack>

              {showDetails && (
                <Typography
                  fontWeight={400}
                  fontSize={{ xs: "11px", md: "14px" }}
                  color="#2F6B5F"
                  mt={2}
                >
                  *The calculation is based on your profile and does not include
                  interest component
                </Typography>
              )}

              {showDetails &&
                isInsuranceOpen &&
                insurances?.map((insurance) => {
                  return (
                    <Stack
                      key={insurance?.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                      mt={1}
                      py={0.75}
                    >
                      <Stack direction="row" alignItems="flex-start" gap={1}>
                        {editOptionToggle && (
                          <>
                            <Switch
                              size="small"
                              checked={insurance?.isActive}
                              onChange={async (e) => {
                                const checked = e.target.checked;
                                const activePlans = insurances
                                  ?.filter((insurance) => insurance?.isActive)
                                  .map((item) => item?.id);
                                const updatedPlans = checked
                                  ? [
                                      ...new Set([
                                        ...activePlans,
                                        insurance?.id,
                                      ]),
                                    ]
                                  : activePlans?.filter(
                                      (item) => item !== insurance?.id,
                                    );

                                const insurancePayload = {
                                  appId: productApplication.appId,
                                  selectedPlan: updatedPlans,
                                  selectOfferRequest: {
                                    offerId:
                                      productApplication?.stage === STAGE.OFFERS
                                        ? offerSelectedData?.id
                                        : 0,
                                    newRequestLoanAmount:
                                      productApplication?.stage === STAGE.OFFERS
                                        ? offerSelectedData?.amount
                                        : 0,
                                    selectedTenure:
                                      productApplication?.stage === STAGE.OFFERS
                                        ? offerSelectedData?.tenure
                                        : 0,
                                  },
                                  insuranceConsent: false,
                                  isConsentRequest: false,
                                };

                                dispatch(setSelectedPlans(updatedPlans));
                                await handleInsuranceUpdate(insurancePayload);
                              }}
                            />
                          </>
                        )}

                        <Box>
                          <Typography
                            fontWeight={400}
                            fontSize={isMobile ? "12px" : "16px"}
                            color="#4D6767"
                            lineHeight="16px"
                          >
                            {insurance?.title}
                          </Typography>

                          {insurance?.validity && (
                            <Typography
                              fontWeight={400}
                              fontSize={{ xs: "10px", md: "12px" }}
                              color="#4D6767CC"
                              lineHeight="14px"
                            >
                              {`(Validity: ${insurance?.validity} Months)`}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {insurance?.amount && (
                        <Typography
                          fontWeight={500}
                          fontSize={isMobile ? "12px" : "16px"}
                          color={WEB_COLORS.darkTeal}
                          whiteSpace="nowrap"
                        >
                          {CurrencyConverter(insurance?.amount, 2)}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}

              {showDetails && insuranceData?.data?.isActive && (
                <Stack mt={1}>
                  <CheckboxComponent
                    name="consent-check"
                    label={
                      <Typography
                        fontWeight={{ md: 350, xs: 400 }}
                        fontSize={{ xs: "10px", md: "14px" }}
                      >
                        I certify that by selecting Insurance the proposed
                        member is in good health, free from mental or physical
                        impairment & do not suffer from any chronic or
                        pre-existing disease.
                      </Typography>
                    }
                    disabled={!insuranceData?.data?.isActive}
                    checked={insuranceDeclaration}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      dispatch(setInsuranceDeclaration(isChecked));
                    }}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </PaperComponent>
      <ModalComponent
        maxWidth={"sm"}
        fullScreen={isMobile}
        open={offerReminder}
        extraFunction={() => {}}
        closeButtonRequired
        handleModal={() => handleLimitedOfferModal()}
        // open={offerReminder}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        contentSx={{
          p: { xs: 1, md: 2 },
        }}
      >
        <LimitedOfferReminder
          baseEmi={
            productApplication?.stage === STAGE.E_SIGN
              ? insuranceData?.data?.baseEmi
              : offerSelectedData?.emi
          }
          amount={amount}
          setInsuranceRequired={offerReminder}
          handleContinueWithInsurance={handleContinueWithInsurance}
          handleContinueWithoutInsurance={handleContinueWithoutInsurance}
        />
      </ModalComponent>
    </>
  );
};

export default InsurancePlan;
